/**
 * @fileoverview listViewScope — shared-terminal isolation for persisted list
 * view-state (LVP-001 / PII-GATE-001 #7).
 *
 * ParcelPuffin (pos-web) runs on shared terminals where the person at the
 * keyboard is frequently NOT the logged-in user. List view-state (column
 * visibility / order / widths / presets / sort / filter values) is cached in
 * per-origin `localStorage`. Without isolation, user A's column choices would be
 * read by user B after A walks away from the terminal. Two complementary
 * defenses, both driven automatically from the auth lifecycle:
 *
 *   1. `setListViewScope(userId)` — every persisted view key is namespaced
 *      `lv:<userId>:<key>` so user B never reads user A's keys (isolation by
 *      construction; survives a missed logout-clear).
 *   2. `clearListViewState()` — wipes all scoped + legacy view keys on logout /
 *      switch-user / token expiry, so the cache never outlives the session.
 *
 * `createAuthTokenStorage` drives both: `setAuth` sets the scope from the
 * authenticated user, `clearAuth` clears the cache and nulls the scope. Every
 * web app therefore gets isolation through its existing auth flow with no extra
 * per-app wiring. `@puffin-core/page-shell`'s `localStoragePersistence` routes
 * every view-state key through `scopedViewKey()`.
 *
 * When no scope is set (logged out, or an app that never authenticates), keys
 * pass through unchanged — so the mechanism is a zero-regression no-op until a
 * user is established.
 *
 * @module @puffin-core/auth-token-storage/listViewScope
 */

/** Prefix applied to every user-scoped view-state key. */
const SCOPE_PREFIX = 'lv:';

/**
 * Matches a `view` dotted segment so legacy (pre-scope) view-state keys are
 * still cleaned up: `sales.view`, `sales.view.columns`, or a bare `view`.
 * `overview` / `review` do NOT match (no dot before `view`).
 */
const LEGACY_VIEW_KEY = /(^|\.)view(\.|$)/;

let activeScope = null;

/**
 * Set the active user scope. Pass a falsy value to clear the scope (keys then
 * pass through unscoped).
 * @param {string|null|undefined} userId
 */
export function setListViewScope(userId) {
  activeScope = userId ? String(userId) : null;
}

/**
 * @returns {string|null} The active user scope, or null when unscoped.
 */
export function getListViewScope() {
  return activeScope;
}

/**
 * Namespace a view-state localStorage key by the active user scope. Returns the
 * key unchanged when no scope is set (zero-regression fallback).
 * @param {string} key
 * @returns {string}
 */
export function scopedViewKey(key) {
  if (!key || !activeScope) return key;
  return `${SCOPE_PREFIX}${activeScope}:${key}`;
}

/**
 * Remove every persisted view-state key (all `lv:`-scoped keys plus any legacy
 * raw `*.view*` keys) and null the active scope.
 *
 * The ONE expected failure is the browser REFUSING Web Storage: the
 * `localStorage` getter throws a `SecurityError` DOMException (HTML Living
 * Standard, "Web storage"). Then this page can reach no stored key, so there is
 * nothing for it to clear. Anything else thrown is a defect and surfaces
 * (SWALLOWED-CATCH-BURNDOWN-001 U3: the old catch swallowed everything).
 */
export function clearListViewState() {
  activeScope = null;
  if (typeof window === 'undefined') return;
  let ls;
  try {
    ls = window.localStorage;
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'SecurityError')) throw error;
    return;
  }
  const toRemove = [];
  for (let i = 0; i < ls.length; i += 1) {
    const k = ls.key(i);
    if (k.startsWith(SCOPE_PREFIX) || LEGACY_VIEW_KEY.test(k)) {
      toRemove.push(k);
    }
  }
  toRemove.forEach((k) => ls.removeItem(k));
}
